// src/services/portfolio.service.ts
import { http } from '../api/http';
import { ENV } from '../config/env';
import type { PortfolioResponse } from '../types/Portfolio';

export type StatementFormat = 'pdf' | 'csv';

export interface DownloadAccountStatementParams {
  from?: string;
  to?: string;
  format?: StatementFormat;
}

export interface DownloadAccountStatementResult {
  blob: Blob;
  filename: string;
  mime: string;
}

function getToken() {
  return localStorage.getItem('auth_token');
}

function buildQuery(params: DownloadAccountStatementParams) {
  const sp = new URLSearchParams();
  if (params.from) sp.set('from', params.from);
  if (params.to) sp.set('to', params.to);

  if (params.format === 'csv') sp.set('format', 'csv');

  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

function deriveFilenameFromHeaders(headers: Headers, fallback: string) {
  const cd = headers.get('content-disposition') || headers.get('Content-Disposition');
  if (!cd) return fallback;

  const match = cd.match(/filename\*?=(?:UTF-8'')?("?)([^";]+)\1/i);
  if (!match?.[2]) return fallback;

  try {
    return decodeURIComponent(match[2]);
  } catch {
    return match[2];
  }
}

function buildFallbackFilename(params: DownloadAccountStatementParams) {
  const from = params.from?.trim() || 'inicio';
  const to = params.to?.trim() || 'hoy';
  const ext = (params.format || 'pdf') === 'csv' ? 'csv' : 'pdf';
  return `estado-de-cuenta_${from}_${to}.${ext}`;
}

function defaultMimeFor(format: StatementFormat) {
  return format === 'csv' ? 'text/csv' : 'application/pdf';
}

export const PortfolioService = {
  getMyPortfolio() {
    return http<PortfolioResponse>('/portfolio/me');
  },

  async downloadAccountStatement(params: DownloadAccountStatementParams = {}): Promise<DownloadAccountStatementResult> {
    const format: StatementFormat = params.format || 'pdf';
    const endpoint = `/portfolio/account-statement${buildQuery(params)}`;
    const url = `${ENV.API_URL}${endpoint}`;
    const token = getToken();

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept: defaultMimeFor(format)
      }
    });

    if (!res.ok) {
      const contentType = res.headers.get('content-type') || '';
      const isJson = contentType.includes('application/json');
      const payload = isJson ? await res.json().catch(() => null) : await res.text().catch(() => '');
      const message = typeof payload === 'string' && payload.trim() ? payload : 'Request failed';
      throw new Error(message);
    }

    const blob = await res.blob();
    const mime = res.headers.get('content-type') || defaultMimeFor(format);
    const filename = deriveFilenameFromHeaders(res.headers, buildFallbackFilename(params));

    return { blob, filename, mime };
  }
};
